import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Search, Trash2, Pencil, ChevronRight, Eye, UserPlus, Loader2, Mail, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ subscription_status: "", subscription_expires: "", new_password: "" });
  const [createTestOpen, setCreateTestOpen] = useState(false);
  const [createTestForm, setCreateTestForm] = useState({ email: "", name: "", seed_data: true, password: "" });
  const [creatingTest, setCreatingTest] = useState(false);
  const [linksDialog, setLinksDialog] = useState(null);  // {user_id, email}
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [linksData, setLinksData] = useState(null);  // {tokens: [...]}
  const [cloneDialog, setCloneDialog] = useState(null);  // source user object
  const [cloneForm, setCloneForm] = useState({ email: "", name: "", password: "" });
  const [cloning, setCloning] = useState(false);
  useEffect(() => { fetchUsers(); }, [page, search]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users?page=${page}&limit=20&search=${encodeURIComponent(search)}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditDialog({ ...user });
    setEditForm({
      subscription_status: user.subscription_status || "trial",
      subscription_expires: user.subscription_expires ? user.subscription_expires.split("T")[0] : "",
      new_password: ""
    });
  };

  const handleSave = async () => {
    const newPwd = editForm.new_password.trim();
    if (newPwd && newPwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${editDialog.user_id}`, {
        subscription_status: editForm.subscription_status,
        subscription_expires: editForm.subscription_expires ? new Date(editForm.subscription_expires).toISOString() : null
      });
      if (newPwd) {
        const res = await axios.post(`${API}/admin/users/${editDialog.user_id}/set-password`, { password: newPwd });
        toast.success(res.data?.message || "Password updated");
      } else {
        toast.success("User updated");
      }
      setEditDialog(null);
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update"); }
  };

  const handleCreateTestUser = async () => {
    const pwd = createTestForm.password.trim();
    if (pwd && pwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      const payload = {
        seed_data: !!createTestForm.seed_data,
      };
      if (createTestForm.email.trim()) payload.email = createTestForm.email.trim();
      if (createTestForm.name.trim()) payload.name = createTestForm.name.trim();
      if (pwd) payload.password = pwd;
      const res = await axios.post(`${API}/admin/users/create-test`, payload);
      const seed = res.data?.seeded || {};
      toast.success(`Test user created: ${res.data?.user?.email} (${seed.employees || 0} employees, ${seed.shifts || 0} shifts, ${seed.rules || 0} rules)${pwd ? " — login enabled" : ""}`);
      setCreateTestOpen(false);
      setCreateTestForm({ email: "", name: "", seed_data: true, password: "" });
      setPage(1);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create test user");
    } finally {
      setCreatingTest(false);
    }
  };

  const handleClone = async () => {
    const pwd = cloneForm.password.trim();
    if (pwd && pwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setCloning(true);
    try {
      const payload = {};
      if (cloneForm.email.trim()) payload.email = cloneForm.email.trim();
      if (cloneForm.name.trim()) payload.name = cloneForm.name.trim();
      if (pwd) payload.password = pwd;
      const res = await axios.post(`${API}/admin/users/${cloneDialog.user_id}/clone`, payload);
      const c = res.data?.copied || {};
      toast.success(
        `Replica created: ${res.data?.user?.email} — ${c.employees || 0} employees, ${c.shifts || 0} shifts, ${c.rules || 0} rules, ${c.rosters || 0} rosters copied${pwd ? " — login enabled" : ""}`
      );
      setCloneDialog(null);
      setCloneForm({ email: "", name: "", password: "" });
      setPage(1);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clone account");
    } finally {
      setCloning(false);
    }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user "${name}" and ALL their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch { toast.error("Failed to delete"); }
  };

  const handleImpersonate = async (userId, name) => {
    try {
      const res = await axios.post(`${API}/admin/impersonate/${userId}`, {});
      const sessionToken = res.data.session_token;
      // Open the main app in a new tab with the impersonation session
      const appUrl = window.location.origin;
      const impersonateUrl = `${appUrl}/dashboard#impersonate=${sessionToken}`;
      window.open(impersonateUrl, '_blank');
      toast.success(`Impersonating ${name} — new tab opened`);
    } catch {
      toast.error("Failed to impersonate user");
    }
  };

  const openLinks = async (user) => {
    setLinksDialog({ user_id: user.user_id, email: user.email });
    setLoadingLinks(true);
    setLinksData(null);
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}/pending-links`);
      setLinksData(res.data);
    } catch {
      toast.error("Failed to load pending links");
      setLinksData({ tokens: [] });
    } finally {
      setLoadingLinks(false);
    }
  };

  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  const getStatusBadge = (status) => {
    const map = {
      active: "bg-emerald-500/10 text-emerald-400",
      trial: "bg-blue-500/10 text-blue-400",
      expired: "bg-red-500/10 text-red-400"
    };
    return map[status] || "bg-zinc-500/10 text-zinc-400";
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Users</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} total accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setCreateTestOpen(true)}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white inline-flex items-center gap-2"
            data-testid="create-test-user-btn"
          >
            <UserPlus className="w-4 h-4" /> New test user
          </Button>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 w-72"
              data-testid="admin-search-input"
            />
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-xs uppercase tracking-[0.15em] text-zinc-500 text-left py-3 px-4">User</th>
              <th className="text-xs uppercase tracking-[0.15em] text-zinc-500 text-left py-3 px-4 hidden md:table-cell">Email</th>
              <th className="text-xs uppercase tracking-[0.15em] text-zinc-500 text-left py-3 px-4">Status</th>
              <th className="text-xs uppercase tracking-[0.15em] text-zinc-500 text-left py-3 px-4 hidden lg:table-cell">Expires</th>
              <th className="text-xs uppercase tracking-[0.15em] text-zinc-500 text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr><td colSpan={5} className="py-8 text-center text-zinc-500">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-zinc-500">No users found</td></tr>
            ) : users.map((user) => (
              <tr key={user.user_id} className="hover:bg-zinc-800/50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8 border border-zinc-700">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xs">{getInitials(user.name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-white">{user.name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 hidden md:table-cell">
                  <span className="text-sm text-zinc-400">{user.email}</span>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded capitalize ${getStatusBadge(user.subscription_status)}`}>
                    {user.subscription_status || "unknown"}
                  </span>
                </td>
                <td className="py-3 px-4 hidden lg:table-cell">
                  <span className="text-xs tabular-nums text-zinc-500">
                    {user.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : "—"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openLinks(user)}
                      className="text-zinc-500 hover:text-amber-400" title="View pending verify/reset links"
                      data-testid={`pending-links-${user.user_id}`}>
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleImpersonate(user.user_id, user.name)}
                      className="text-zinc-500 hover:text-[#002FA7]" title="Impersonate user"
                      data-testid={`impersonate-user-${user.user_id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setCloneDialog(user); setCloneForm({ email: "", name: "" }); }}
                      className="text-zinc-500 hover:text-emerald-400" title="Clone as test account (copies all data)"
                      data-testid={`clone-user-${user.user_id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(user)}
                      className="text-zinc-500 hover:text-white" data-testid={`edit-user-${user.user_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user.user_id, user.name)}
                      className="text-zinc-500 hover:text-red-400" data-testid={`delete-user-${user.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 20 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-zinc-500">Page {page} of {Math.ceil(total / 20)}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="border-zinc-700 text-zinc-400">Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}
              className="border-zinc-700 text-zinc-400">Next</Button>
          </div>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-white">
              Edit User: {editDialog?.name}
            </DialogTitle>
          </DialogHeader>
          {editDialog && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div className="bg-zinc-800 p-3 rounded sm:col-span-2">
                <p className="text-xs text-zinc-500">Email</p>
                <p className="text-sm text-white">{editDialog.email}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">Subscription Status</Label>
                <Select value={editForm.subscription_status}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, subscription_status: v }))}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="edit-sub-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">Subscription Expires</Label>
                <Input type="date" value={editForm.subscription_expires}
                  onChange={(e) => setEditForm(prev => ({ ...prev, subscription_expires: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-sub-expires" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-zinc-500">Set New Password</Label>
                <Input type="password" value={editForm.new_password}
                  placeholder="Leave blank to keep current password"
                  autoComplete="new-password"
                  onChange={(e) => setEditForm(prev => ({ ...prev, new_password: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                  data-testid="edit-user-password-input" />
                <p className="text-[11px] text-zinc-500 mt-1">
                  Min 8 characters. Changing it signs the user out of all their sessions.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-2 sm:col-span-2">
                <Button variant="outline" onClick={() => setEditDialog(null)}
                  className="border-zinc-700 text-zinc-400">Cancel</Button>
                <Button onClick={handleSave} className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-user-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Clone account as test user */}
      <Dialog open={!!cloneDialog} onOpenChange={(o) => { if (!o) setCloneDialog(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-2xl" data-testid="clone-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-white">
              Clone account: {cloneDialog?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
            <p className="text-xs text-zinc-400 sm:col-span-2">
              Creates a new test account that replicates this account's teams, staff, shift types,
              rules, time off, operations, templates, rosters and settings. The copy is fully
              independent — changes to it never affect the original.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Email (optional)</Label>
              <Input
                placeholder="Auto: test+xxxxxx@roster.ai"
                value={cloneForm.email}
                onChange={(e) => setCloneForm({ ...cloneForm, email: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                data-testid="clone-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Name (optional)</Label>
              <Input
                placeholder={`Auto: ${cloneDialog?.name || "User"} (Test copy)`}
                value={cloneForm.name}
                onChange={(e) => setCloneForm({ ...cloneForm, name: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                data-testid="clone-name-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Password (optional)</Label>
              <Input
                type="password"
                placeholder="Min 8 chars — blank = impersonate-only"
                autoComplete="new-password"
                value={cloneForm.password}
                onChange={(e) => setCloneForm({ ...cloneForm, password: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                data-testid="clone-password-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2 sm:col-span-2">
              <Button variant="outline" onClick={() => setCloneDialog(null)}
                className="border-zinc-700 text-zinc-400">Cancel</Button>
              <Button onClick={handleClone} disabled={cloning}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white inline-flex items-center gap-2"
                data-testid="clone-submit-btn">
                {cloning && <Loader2 className="w-4 h-4 animate-spin" />}
                Create replica
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createTestOpen} onOpenChange={setCreateTestOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-2xl" data-testid="create-test-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">New test user</DialogTitle>
          </DialogHeader>
          <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
            <p className="text-sm text-zinc-400 sm:col-span-2">
              Creates a fresh user with a 30-day trial. Optional seed data populates 5 employees, 3 shifts, and 3 baseline rules so you can immediately generate rosters.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Email (optional)</Label>
              <Input
                type="email"
                placeholder="auto-generated if blank"
                value={createTestForm.email}
                onChange={(e) => setCreateTestForm({ ...createTestForm, email: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                data-testid="create-test-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Name (optional)</Label>
              <Input
                type="text"
                placeholder="auto-generated if blank"
                value={createTestForm.name}
                onChange={(e) => setCreateTestForm({ ...createTestForm, name: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                data-testid="create-test-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Password (optional)</Label>
              <Input
                type="password"
                placeholder="Min 8 chars — set to allow direct login"
                autoComplete="new-password"
                value={createTestForm.password}
                onChange={(e) => setCreateTestForm({ ...createTestForm, password: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                data-testid="create-test-password-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Leave blank for an impersonate-only account (no direct login).
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer sm:self-center sm:mt-5">
              <input
                type="checkbox"
                checked={createTestForm.seed_data}
                onChange={(e) => setCreateTestForm({ ...createTestForm, seed_data: e.target.checked })}
                className="accent-[#002FA7]"
                data-testid="create-test-seed-checkbox"
              />
              Seed sample employees, shifts and rules
            </label>
            <div className="flex justify-end gap-2 pt-2 sm:col-span-2">
              <Button variant="outline" onClick={() => setCreateTestOpen(false)}
                className="border-zinc-700 text-zinc-400">Cancel</Button>
              <Button onClick={handleCreateTestUser} disabled={creatingTest}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white inline-flex items-center gap-2"
                data-testid="confirm-create-test-user-btn">
                {creatingTest ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linksDialog} onOpenChange={(open) => !open && setLinksDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl" data-testid="pending-links-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-4 h-4 text-amber-400" /> Pending links for {linksDialog?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-zinc-400">
              Use these links to verify/reset for users when email delivery is broken (e.g. Resend domain not yet verified). Only share with the account owner.
            </p>
            {loadingLinks ? (
              <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></div>
            ) : linksData?.tokens?.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">No pending links for this user.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {linksData?.tokens?.map((t) => {
                  const path = t.purpose === "verify_email" ? "verify-email" : "reset-password";
                  const link = `${window.location.origin}/${path}?token=${t.token}`;
                  return (
                    <div key={t.token_id} className="border border-zinc-800 rounded p-3 bg-black/30" data-testid={`pending-link-${t.token_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${t.purpose === "verify_email" ? "bg-emerald-900/40 text-emerald-300" : "bg-blue-900/40 text-blue-300"}`}>
                          {t.purpose === "verify_email" ? "Verify email" : "Password reset"}
                        </span>
                        <span className="text-[10px] text-zinc-500">Expires: {t.expires_at}</span>
                      </div>
                      <input
                        type="text"
                        readOnly
                        value={link}
                        onClick={(e) => e.target.select()}
                        className="w-full text-xs font-mono px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-zinc-300 cursor-text"
                        data-testid={`pending-link-url-${t.token_id}`}
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-zinc-500">
                          Email sent: <span className={t.email_sent ? "text-emerald-400" : "text-red-400"}>{String(!!t.email_sent)}</span>
                          {t.email_error && <span className="text-red-400"> · {t.email_error.slice(0, 60)}</span>}
                        </span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }}
                          className="text-xs text-[#002FA7] hover:underline"
                          data-testid={`copy-link-${t.token_id}`}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;

